import { useGetPricingMatrixQuery, useLazyGetPricingMatrixCsvQuery } from "../services/reportsV2Api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";

export default function PricingMatrix() {
  const { data: matrix, isLoading } = useGetPricingMatrixQuery();
  const [triggerExportCsv, { isLoading: isExporting }] = useLazyGetPricingMatrixCsvQuery();

  const handleExportCsv = async () => {
    try {
      const blob = await triggerExportCsv().unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Pricing_Matrix_${format(new Date(), "yyyyMMdd")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("CSV export started");
    } catch (err) {
      toast.error("Failed to export CSV");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pricing Matrix</h1>
          <p className="text-muted-foreground">
            Pricing structure across all clinics and service types.
          </p>
        </div>
        <Button 
          variant="outline" 
          className="gap-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
          onClick={handleExportCsv}
          disabled={isExporting}
        >
          {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export CSV
        </Button>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="min-w-[200px] border-r">Clinic Location</TableHead>
                    {matrix?.services?.map((service: string) => (
                      <TableHead key={service} className="text-center min-w-[180px] border-r">
                        {service}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matrix?.rows?.map((row: any) => (
                    <TableRow key={row.clinicId}>
                      <TableCell className="font-medium border-r">
                        <div>{row.clinicName}</div>
                        <div className="text-xs text-muted-foreground">{row.city}, {row.state}</div>
                      </TableCell>
                      {matrix.services.map((service: string) => {
                        const pricing = row.pricing[service];
                        return (
                          <TableCell key={service} className="text-center border-r text-[11px] p-2">
                            {pricing ? (
                              <div className="grid grid-cols-1 gap-0.5">
                                <div className="text-muted-foreground flex justify-between">
                                  <span>Cost:</span> <span>${pricing.vendorPrice}</span>
                                </div>
                                <div className="text-emerald-600 flex justify-between font-medium">
                                  <span>Margin:</span> <span>+${pricing.margin}</span>
                                </div>
                                <div className="border-t mt-0.5 pt-0.5 font-bold flex justify-between text-sm">
                                  <span>Price:</span> <span>${pricing.clientPrice}</span>
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
